import { useCallback, useContext, useEffect, useState } from 'react';
import { IEditorProps } from './Editor';
import { useEditorSettings } from 'components/property-editors/hooks/useEditorSettings';
import { EditorLabel } from './EditorLabel';
import { useEditorId } from './hooks/useEditorId';
import { TypeComboPicker } from 'components/inputs/TypeComboPicker';
import { PropertyEditorContext } from 'components/property-editors/contexts/PropertyEditorContext';
import { DecisionsType } from '@decisions/server/DecisionsFramework/Design/Flow/Mapping';
import { getDecisionsTypeId } from 'utils/DecisionsTypeUtils';
import { clear, getPickedEntityId, submit } from './TypePickerEditorHelper';
import { getEditorValidationSummary } from 'utils/ValidationUtils';
import { AwaitableContext } from 'contexts/AwaitableContext';

interface ITypePickerEditorProps extends IEditorProps {}

/**
 * Copyright (c) 2020 Decisions All Rights Reserved
 */
export function TypePickerEditor({ child, path }: ITypePickerEditorProps) {
  const [entityId, setEntityId] = useState<string | null>(getPickedEntityId(child));

  const [id] = useEditorId(path);
  const { editingContextId } = useContext(PropertyEditorContext);
  const validation = getEditorValidationSummary(child);
  const settings = useEditorSettings(child);
  const { addPromise, removePromise } = useContext(AwaitableContext);

  useEffect(() => {
    const entityId = getPickedEntityId(child);
    setEntityId(entityId);
  }, [child]);

  const selectTypeById = useCallback(
    (typeId: string | null) => {
      setEntityId(typeId);

      const id = path.join(',');

      addPromise(
        id,
        (async () => {
          try {
            if (typeId != null) {
              await submit(editingContextId, path, typeId);
            } else {
              await clear(editingContextId, path);
            }
          } finally {
            removePromise(id);
          }
        })()
      );
    },
    [addPromise, editingContextId, path, removePromise]
  );

  const handleSelected = useCallback(
    async (type: DecisionsType | null) => {
      let typeId: string | null = null;
      if (type) {
        typeId = getDecisionsTypeId(type);
      }
      selectTypeById(typeId);
    },
    [selectTypeById]
  );

  return (
    <EditorLabel htmlFor={id} editor={child} path={path} className={'dp-type-picker-editor'}>
      <TypeComboPicker
        pickedEntity={entityId}
        settings={settings}
        validation={validation}
        onSelected={handleSelected}
      />
    </EditorLabel>
  );
}
